<template>
  <div id="app">

    <!-- Render the crossword plugin with a ref, clues, and disabled prop -->
    <CrosswordPlugin 
      ref="crosswordPlugin" 
      :grid="crosswordGrid" 
      :clues="clues" 
      :disabled="editingDisabled"
      title="Today's Crossword Puzzle" 
      @solvedCluesUpdated="updateSolvedClues"
    />

    <!-- Clues are displayed underneath the crossword -->
    <div class="clues-section">
      <h3>Clues</h3>
      <div class="clue-group">
        <h4>Across</h4>
        <ul>
          <li
            v-for="(clue, index) in clues.across"
            :key="'across-' + index"
            @click="handleClueClick(clue, 'across')"
            :class="{'clickable-clue': true, solved: solvedClues.across.includes(clue.number)}"
          >
            <strong>{{ clue.number }}.</strong> {{ clue.text }}
          </li>
        </ul>
      </div>
      <div class="clue-group">
        <h4>Down</h4>
        <ul>
          <li
            v-for="(clue, index) in clues.down"
            :key="'down-' + index"
            @click="handleClueClick(clue, 'down')"
            :class="{'clickable-clue': true, solved: solvedClues.down.includes(clue.number)}"
          >
            <strong>{{ clue.number }}.</strong> {{ clue.text }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Deployment instructions (for demonstration) -->
    <div class="deployment-info">
      <h3>Deployment</h3>
      <p>
        To deploy your Vue.js crossword on Cloudflare Pages, simply push your code (with this App.vue)
        to your connected Git repository. Cloudflare Pages will automatically build your project using
        <code>npm run build</code> and deploy it. Make sure your domain (monstercrosswords.com) is connected
        via Cloudflare’s dashboard.
      </p>
    </div>
  </div>
</template>

<script>
import CrosswordPlugin from './components/CrosswordPlugin.vue';

export default {
  name: "App",
  components: { CrosswordPlugin },
  data() {
    return {
      // Crossword grid data (13 columns per row) – adjust as needed.
      crosswordGrid: [
        // Row 0
        [
          { isBlack: false, answer: "S", userValue: "", number: 1 },
          { isBlack: false, answer: "A", userValue: "" },
          { isBlack: false, answer: "V", userValue: "", number: 2 },
          { isBlack: false, answer: "A", userValue: "" },
          { isBlack: false, answer: "G", userValue: "", number: 3 },
          { isBlack: false, answer: "E", userValue: "" },
          null,
          { isBlack: false, answer: "L", userValue: "", number: 4 },
          { isBlack: false, answer: "A", userValue: "", number: 5 },
          { isBlack: false, answer: "N", userValue: "" },
          { isBlack: false, answer: "D", userValue: "", number: 6 },
          { isBlack: false, answer: "E", userValue: "" },
          { isBlack: false, answer: "D", userValue: "", number: 7 },
        ],
        // Row 1
        [
          { isBlack: false, answer: "A", userValue: "" },
          null,
          { isBlack: false, answer: "I", userValue: "" },
          null,
          { isBlack: false, answer: "L", userValue: "" },
          null,
          { isBlack: false, answer: "I", userValue: "", number: 8 },
          null,
          { isBlack: false, answer: "R", userValue: "" },
          null,
          { isBlack: false, answer: "I", userValue: "" },
          null,
          { isBlack: false, answer: "A", userValue: "" },
        ],
        // Row 2
        [
          { isBlack: false, answer: "F", userValue: "", number: 9 },
          { isBlack: false, answer: "O", userValue: "" },
          { isBlack: false, answer: "G", userValue: "" },
          { isBlack: false, answer: "H", userValue: "" },
          { isBlack: false, answer: "O", userValue: "" },
          { isBlack: false, answer: "R", userValue: "" },
          { isBlack: false, answer: "N", userValue: "" },
          null,
          { isBlack: false, answer: "G", userValue: "", number: 10 },
          { isBlack: false, answer: "R", userValue: "" },
          { isBlack: false, answer: "A", userValue: "" },
          { isBlack: false, answer: "V", userValue: "" },
          { isBlack: false, answer: "Y", userValue: "" },
        ],
        // Row 3
        [
          { isBlack: false, answer: "E", userValue: "" },
          null,
          { isBlack: false, answer: "I", userValue: "" },
          null,
          { isBlack: false, answer: "S", userValue: "" },
          null,
          { isBlack: false, answer: "C", userValue: "" },
          null,
          { isBlack: false, answer: "O", userValue: "" },
          null,
          { isBlack: false, answer: "R", userValue: "" },
          null,
          { isBlack: false, answer: "B", userValue: "" },
        ],
        // Row 4
        [
          { isBlack: false, answer: "L", userValue: "", number: 11 },
          { isBlack: false, answer: "U", userValue: "" },
          { isBlack: false, answer: "L", userValue: "" },
          { isBlack: false, answer: "L", userValue: "" },
          { isBlack: false, answer: "S", userValue: "" },
          null,
          { isBlack: false, answer: "R", userValue: "", number: 12 },
          { isBlack: false, answer: "E", userValue: "" },
          { isBlack: false, answer: "S", userValue: "" },
          { isBlack: false, answer: "P", userValue: "" },
          { isBlack: false, answer: "I", userValue: "" },
          { isBlack: false, answer: "R", userValue: "" },
          { isBlack: false, answer: "E", userValue: "" },
        ],
        // Row 5
        [
          { isBlack: false, answer: "Y", userValue: "" },
          null,
          null,
          null,
          { isBlack: false, answer: "E", userValue: "" },
          null,
          { isBlack: false, answer: "I", userValue: "" },
          null,
          null,
          null,
          { isBlack: false, answer: "S", userValue: "" },
          null,
          { isBlack: false, answer: "D", userValue: "" },
        ],
        // Row 6
        [
          null,
          { isBlack: false, answer: "D", userValue: "", number: 13 },
          { isBlack: false, answer: "I", userValue: "", number: 14 },
          { isBlack: false, answer: "S", userValue: "" },
          { isBlack: false, answer: "S", userValue: "" },
          { isBlack: false, answer: "E", userValue: "" },
          { isBlack: false, answer: "M", userValue: "" },
          { isBlack: false, answer: "I", userValue: "" },
          { isBlack: false, answer: "N", userValue: "", number: 15 },
          { isBlack: false, answer: "A", userValue: "" },
          { isBlack: false, answer: "T", userValue: "" },
          { isBlack: false, answer: "E", userValue: "" },
          null,
        ],
        // Row 7
        [
          { isBlack: false, answer: "S", userValue: "", number: 16 },
          null,
          { isBlack: false, answer: "M", userValue: "" },
          null,
          null,
          null,
          { isBlack: false, answer: "I", userValue: "" },
          null,
          { isBlack: false, answer: "E", userValue: "" },
          null,
          null,
          null,
          { isBlack: false, answer: "M", userValue: "", number: 17 },
        ],
        // Row 8
        [
          { isBlack: false, answer: "C", userValue: "", number: 18 },
          { isBlack: false, answer: "O", userValue: "" },
          { isBlack: false, answer: "A", userValue: "" },
          { isBlack: false, answer: "R", userValue: "" },
          { isBlack: false, answer: "S", userValue: "", number: 19 },
          { isBlack: false, answer: "E", userValue: "" },
          { isBlack: false, answer: "N", userValue: "" },
          null,
          { isBlack: false, answer: "U", userValue: "", number: 20 },
          { isBlack: false, answer: "N", userValue: "" },
          { isBlack: false, answer: "A", userValue: "", number: 21 },
          { isBlack: false, answer: "M", userValue: "" },
          { isBlack: false, answer: "I", userValue: "" },
        ],
        // Row 9
        [
          { isBlack: false, answer: "Y", userValue: "" },
          null,
          { isBlack: false, answer: "G", userValue: "" },
          null,
          { isBlack: false, answer: "H", userValue: "" },
          null,
          { isBlack: false, answer: "A", userValue: "" },
          null,
          { isBlack: false, answer: "T", userValue: "" },
          null,
          { isBlack: false, answer: "L", userValue: "" },
          null,
          { isBlack: false, answer: "S", userValue: "" },
        ],
        // Row 10
        [
          { isBlack: false, answer: "T", userValue: "", number: 22 },
          { isBlack: false, answer: "R", userValue: "" },
          { isBlack: false, answer: "I", userValue: "" },
          { isBlack: false, answer: "B", userValue: "" },
          { isBlack: false, answer: "E", userValue: "" },
          null,
          { isBlack: false, answer: "T", userValue: "", number: 23 },
          { isBlack: false, answer: "U", userValue: "" },
          { isBlack: false, answer: "R", userValue: "" },
          { isBlack: false, answer: "N", userValue: "" },
          { isBlack: false, answer: "O", userValue: "" },
          { isBlack: false, answer: "F", userValue: "" },
          { isBlack: false, answer: "F", userValue: "" },
        ],
        // Row 11
        [
          { isBlack: false, answer: "H", userValue: "" },
          null,
          { isBlack: false, answer: "N", userValue: "" },
          null,
          { isBlack: false, answer: "E", userValue: "" },
          null,
          { isBlack: false, answer: "E", userValue: "" },
          null,
          { isBlack: false, answer: "A", userValue: "" },
          null,
          { isBlack: false, answer: "N", userValue: "" },
          null,
          { isBlack: false, answer: "I", userValue: "" },
        ],
        // Row 12
        [
          { isBlack: false, answer: "E", userValue: "", number: 24 },
          { isBlack: false, answer: "X", userValue: "" },
          { isBlack: false, answer: "E", userValue: "" },
          { isBlack: false, answer: "M", userValue: "" },
          { isBlack: false, answer: "P", userValue: "" },
          { isBlack: false, answer: "T", userValue: "" },
          null,
          { isBlack: false, answer: "A", userValue: "", number: 25 },
          { isBlack: false, answer: "L", userValue: "" },
          { isBlack: false, answer: "B", userValue: "" },
          { isBlack: false, answer: "E", userValue: "" },
          { isBlack: false, answer: "I", userValue: "" },
          { isBlack: false, answer: "T", userValue: "" },
        ],
      ],
      clues: {
        across: [
          { number: 1, text: "Harshly critical or violent (6)" },
          { number: 4, text: "Came back down to earth (6)" },
          { number: 9, text: "Warning to sailors (7)" },
          { number: 10, text: "Train or boat? (5)" },
          { number: 11, text: "Periods of calm (5)" },
          { number: 12, text: "Breathe (7)" },
          { number: 13, text: "Spread - seed in a mist (anag) (11)" },
          { number: 18, text: "Get rougher (7)" },
          { number: 20, text: "Soy sauce flavour (5)" },
          { number: 22, text: "Family (5)" },
          { number: 23, text: "It makes one lose interest (4-3)" },
          { number: 24, text: "Not liablie (6)" },
          { number: 25, text: "Even though" },
        ],
        down: [
          { number: 1, text: "Without experiencing harm (6)" },
          { number: 2, text: "Time to watch and wait (5)" },
          { number: 3, text: "Interpretations (of a text) (7)" },
          { number: 5, text: "Old dog of Odysseus - shop once known for its catalogues" },
          { number: 6, text: "Bridget Jones, say (7)" },
          { number: 7, text: "Sleep, or sit, on it (6)" },
          { number: 8, text: "Make (someone) look culpable (11)" },
          { number: 14, text: "Suppose (7)" },
          { number: 15, text: "Non-partisan (7)" },
          { number: 16, text: "Grim Reaper's implement (6)" },
          { number: 17, text: "One's out of place (6)" },
          { number: 19, text: "Wool producer(s) (5)" },
          { number: 21, text: "Solitary (5)" },
        ],
      },
      // Timer and editing state:
      timer: 1200, // 20 minutes in seconds
      timerInterval: null,
      editingDisabled: false,
      // Holds solved clue numbers (emitted via solvedCluesUpdated)
      solvedClues: { across: [], down: [] },
    };
  },
  computed: {
    formattedTime() {
      const minutes = Math.floor(this.timer / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (this.timer % 60).toString().padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
  },
  methods: {
    handleClueClick(clue, orientation) {
      this.$refs.crosswordPlugin.activateClue(clue.number, orientation);
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        this.timer--;
        if (this.timer <= 0) {
          clearInterval(this.timerInterval);
          window.alert("Time's up!");
          this.fillInAnswers();
          this.editingDisabled = true;
        }
      }, 1000);
    },
    fillInAnswers() {
      // Auto-fill every white cell with its correct answer.
      this.crosswordGrid = this.crosswordGrid.map(row =>
        row.map(cell => {
          if (cell && !cell.isBlack) {
            return { ...cell, userValue: cell.answer };
          }
          return cell;
        })
      );
    },
    updateSolvedClues(solved) {
      this.solvedClues = solved;
    },
  },
  mounted() {
    this.startTimer();
  },
};
</script>

<style>
#app {
  text-align: center;
  font-family: Arial, sans-serif;
}
.site-logo {
  height: 100px;
  width: auto;
  margin-right: 20px;
}
.timer {
  font-size: 18px;
  font-weight: bold;
  margin-top: 10px;
}
.clues-section {
  margin: 20px auto 0;
  text-align: left;
  max-width: 600px;
  border-top: 1px solid #ccc;
  padding-top: 10px;
}
.clue-group {
  margin-bottom: 20px;
}
.clickable-clue {
  cursor: pointer;
  color: blue;
  text-decoration: underline;
}
.clickable-clue.solved {
  text-decoration: line-through;
  color: gray;
}
.deployment-info {
  margin: 20px auto;
  max-width: 600px;
  font-size: 14px;
  color: #555;
  text-align: left;
}
</style>